import React, { useState } from 'react';
import { 
  Home, 
  ChevronRight, 
  Share2, 
  Copy, 
  Check, 
  Sparkles,
  ArrowLeft
} from 'lucide-react';
import { navigateTo } from '../utils/routes';

interface PageHeaderBannerProps {
  pageTitle: string;
  badge?: string;
  description?: string;
  breadcrumbs?: { label: string; path?: string }[];
  currentPath: string;
  category?: string;
}

export const PageHeaderBanner: React.FC<PageHeaderBannerProps> = ({
  pageTitle,
  badge,
  description,
  breadcrumbs = [],
  currentPath,
  category
}) => {
  const [copied, setCopied] = useState(false);

  const fullUrl = `https://dojapao.co.jp${currentPath.startsWith('/') ? currentPath : '/' + currentPath}`;

  const handleCopyLink = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(fullUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch (err) {
      console.warn('Could not copy link to clipboard:', err);
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: pageTitle,
          text: description || pageTitle,
          url: fullUrl
        });
      } catch (err) {
        // User cancelled or unsupported
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="bg-gradient-to-b from-white to-slate-50/80 border-b border-slate-200/80 mb-6 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Left: Breadcrumbs & Page Identity */}
        <div>
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium mb-1.5 flex-wrap">
            <button
              onClick={() => navigateTo('/')}
              className="inline-flex items-center gap-1 hover:text-[#1D3557] transition-colors cursor-pointer"
            >
              <Home className="w-3.5 h-3.5 text-slate-400" />
              <span>Início</span>
            </button>

            {category && (
              <>
                <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
                <span className="text-slate-400">{category}</span>
              </>
            )}

            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
                {crumb.path ? (
                  <button
                    onClick={() => navigateTo(crumb.path!)}
                    className="hover:text-[#1D3557] transition-colors cursor-pointer"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="text-slate-700 font-bold">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}

            <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
            <span className="text-[#1D3557] font-bold truncate max-w-[200px] sm:max-w-none">
              {pageTitle}
            </span>
          </nav>

          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-black text-[#1D3557] tracking-tight">
              {pageTitle}
            </h1>
            {badge && (
              <span className="px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wider bg-rose-50 text-[#E63946] border border-rose-200/80 rounded-full shadow-2xs">
                {badge}
              </span>
            )}
          </div>

          {description && (
            <p className="text-xs sm:text-sm text-slate-600 mt-1 max-w-3xl leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {/* Right: Direct Page Link & Share Actions */}
        <div className="flex items-center gap-2 self-start md:self-center shrink-0">
          <button
            onClick={() => navigateTo('/')}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 rounded-xl transition-all shadow-2xs cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Voltar</span>
          </button>

          <button
            onClick={handleCopyLink}
            title="Copiar link direto desta página"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200/90 rounded-xl transition-all shadow-2xs cursor-pointer active:scale-95"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-emerald-700">Link Copiado!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 text-slate-400" />
                <span className="hidden sm:inline">Copiar Link</span>
                <span className="sm:hidden">Copiar</span>
              </>
            )}
          </button>

          <button
            onClick={handleNativeShare}
            title="Compartilhar página"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-[#1D3557] hover:bg-[#152741] rounded-xl transition-all shadow-2xs cursor-pointer active:scale-95"
          >
            <Share2 className="w-3.5 h-3.5 text-amber-400" />
            <span>Compartilhar</span>
          </button>
        </div>
      </div>
    </div>
  );
};
